/**
 * 運営ポータルの枠＝**サイドバー IA**（M1.7・D-91）＋トップバー（email + ログアウト）＋本文（Outlet）。
 *
 * ★ M1.7 で水平タブ列から**サイドバー形式**に変えた（森井氏依頼）＝卸コンソール（apps/console）と
 *   同じ骨格（CSS Grid `"side top" / "side main"`）にすることで、2つの管理画面を行き来しても
 *   「どこに何があるか」の当たりが同じになる。**副題「運営ポータル」**をサイドバーの上に置くのは、
 *   見た目が卸コンソールとそっくりになる以上、**取り違えの標識が要る**ため（片方は他社のデータを
 *   横断で見る画面なので、間違いの代償が大きい）。
 *
 * ★ **モバイル対応は作らない**（設計 08 §2＝運営は2名・PC 前提）。狭い画面では横スクロールを許容する。
 *   `useIsMobile` 相当を持ち込むと、console と同じ「両方を DOM に置かない」保守が ops にも増える。
 *
 * ★ NAV の `testid` と `end` は M0 S3 から変えない（`e2e/ops-monitoring.spec.ts` が
 *   nav-overview / nav-usage / nav-tenants / nav-audit を直接 click する）。
 */

import { auth, ops, type OpsMe } from "@kenzai/api-client";
import { Icon, type IconName } from "@kenzai/ui";
import { useQuery } from "@tanstack/react-query";
import { NavLink, Outlet } from "react-router-dom";

type Item = { to: string; label: string; testid: string; end?: boolean; icon: IconName };
type Group = { title: string; items: Item[] };

/**
 * グローバルナビ（08 §5＝テナント管理・監視・利用状況・監査・発注代行）。
 *
 * ★**ラベルは1文字も変えない**（手順書・stg 台本がこの語彙で書かれている）。
 *   M1.7 で変えたのは「並べ方（グループ化）」と「見た目」だけ。
 */
const GROUPS: Group[] = [
  {
    title: "監視",
    items: [
      { to: "/overview", label: "概況", testid: "nav-overview", icon: "dash" },
      { to: "/usage", label: "利用状況", testid: "nav-usage", icon: "list" },
    ],
  },
  {
    title: "テナント",
    // `end` は必須＝`/` は全ルートの前方一致になるので、これが無いと常に現在地になる
    items: [{ to: "/", label: "テナント", testid: "nav-tenants", end: true, icon: "building" }],
  },
  {
    // 08 §10（T-D40・D-69）＝卸から届いた「メーカーへの発注をお願いしたい」を捌く場所
    title: "業務",
    items: [{ to: "/requests", label: "発注代行", testid: "nav-requests", icon: "bag" }],
  },
  {
    title: "記録",
    items: [{ to: "/audit", label: "監査ログ", testid: "nav-audit", icon: "shield" }],
  },
];

/**
 * 発注代行の未完了件数（サイドバーの件数ピル）。
 *
 * ★**専用のカウント API を作らない**（console の Shell と同じ考え方）＝一覧と別の数え方が
 *   生まれると必ずずれる。RequestQueue と**同じ一覧 API・同じ絞り込み・同じ間隔**で引く。
 * ★ 運営の応答には `badge_count` が無い（console 側だけが返す）ので `items.length` を数える。
 *   既定 limit は 50 で頭打ちになるため **100 を明示**し、それでも溢れる（`next_cursor` が
 *   残る／100 に到達した）ときは「99+」に丸める（console の BadgeDot と同じ見た目）。
 * ★ キーは `["ops-requests", …]` 接頭で揃える＝RequestQueue の
 *   `invalidateQueries({ queryKey: ["ops-requests"] })`（前方一致）でピルも一緒に更新される。
 *   ただし**末尾を分ける**＝同じキーに別の queryFn を相乗りさせると、どちらの取り方で
 *   fetch されるかが観測者の登録順しだいになる。
 */
function useRequestQueueCount(): { count: number; capped: boolean } {
  const q = useQuery({
    queryKey: ["ops-requests", "nav-count"],
    // ★**一覧（RequestQueue）と同じ条件で数える**＝limit を指定せずサーバ既定に任せる。
    //   ここだけ limit を広げると、一覧が 1 ページ目しか出さない以上「ピルは 60 と言うのに
    //   画面には 50 件しか無く、残りへ行く道も無い」という食い違いを作る（M1.7 の敵対レビューで検出）。
    //   数えきれない分は `next_cursor` の有無で分かるので、そのときは「+」を付けて正直に出す。
    queryFn: () => ops.requests({ status: ["open", "in_progress"] }),
    refetchInterval: 60_000,
  });
  const count = q.data?.items.length ?? 0;
  return { count, capped: q.data?.next_cursor != null };
}

export function Shell({ me, onLogout }: { me: OpsMe; onLogout: () => void }) {
  const requests = useRequestQueueCount();

  return (
    // console（apps/console/src/Shell.tsx）と同じ骨格＝サイドバー＋トップバー＋main の2行グリッド
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "var(--kz-sidebar-w) 1fr",
        gridTemplateRows: "var(--kz-topbar-h) 1fr",
        gridTemplateAreas: '"side top" "side main"',
        minHeight: "100dvh",
      }}
    >
      <aside
        style={{
          gridArea: "side",
          background: "var(--kz-sidenav-bg)",
          color: "var(--kz-sidenav-fg-strong)",
          padding: "var(--kz-space-4) 0",
          display: "flex",
          flexDirection: "column",
          // ★**画面に貼り付ける**＝縦に長いページ（発注代行のカード一覧など）でスクロールすると
          //   メニューが上へ流れて消え、幅 236px の暗い列だけが残っていた（M1.7 の敵対レビューで実測）。
          //   `align-self: start` が要る＝grid の既定 stretch では高さがページ全体になり、
          //   「貼り付く先」が無くなって sticky が効かない。
          position: "sticky",
          top: 0,
          alignSelf: "start",
          height: "100dvh",
        }}
      >
        <div style={{ padding: "0 22px var(--kz-space-4)" }}>
          <div style={{ fontWeight: 700, fontSize: "var(--kz-font-size-lg)" }}>建材オーダー</div>
          {/* ★卸コンソールと取り違えないための標識（骨格が同じになった以上、ここが唯一の目印） */}
          <div style={{ fontSize: 11.5, color: "var(--kz-sidenav-fg-muted)" }}>運営ポータル</div>
        </div>

        <nav aria-label="メイン" style={{ flex: 1, overflowY: "auto" }}>
          {GROUPS.map((g) => (
            <div key={g.title}>
              <div className="kz-nav-group">{g.title}</div>
              {g.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  data-testid={it.testid}
                  className={({ isActive }) => (isActive ? "kz-nav-item is-active" : "kz-nav-item")}
                >
                  <Icon name={it.icon} />
                  {it.label}
                  {/* 件数は**溜まっている物**にだけ出す（全項目に出すと意味が薄れる） */}
                  {it.to === "/requests" && requests.count > 0 && (
                    <span className="kz-nav-count" data-testid="nav-count-requests">
                      {/* 1ページに収まらないときは「50+」＝一覧が実際に見せられる数と揃える */}
                      {requests.capped ? `${requests.count}+` : requests.count}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
      </aside>

      <header className="kz-topbar" style={{ gridArea: "top" }}>
        <span style={{ flex: 1 }} />
        {/* ★`ops-email` は**メールアドレスだけ**を含む要素に付ける＝4つの spec が
            `toHaveText("dev-ops@potential-x.com")` で**完全一致**を見ている。
            役割や区切り文字を同じ要素に入れた瞬間に落ちる。 */}
        <span style={{ fontSize: "var(--kz-font-size-sm)", color: "var(--kz-color-ink-muted)" }}>
          <span data-testid="ops-email">{me.email}</span> ／{" "}
          {me.role === "ops_admin" ? "管理者" : "メンバー"}
        </span>
        <button
          type="button"
          className="kz-btn kz-btn--ghost kz-btn--sm"
          onClick={async () => {
            await auth.opsLogout().catch(() => undefined);
            onLogout();
          }}
        >
          ログアウト
        </button>
      </header>

      {/* max-width＝ops の表は console より列が少ない（5列前後）ので 1380 だと間延びする */}
      <main
        style={{
          gridArea: "main",
          padding: "26px 30px 60px",
          maxWidth: 1140,
          width: "100%",
          minWidth: 0,
        }}
      >
        <Outlet />
      </main>
    </div>
  );
}
